import { Fragment, useEffect, useState } from "react";
import { getPrice } from "../../utils/translator";
import { Translate } from "../ChoosePlanCarePlus/planContent";
import { CarePlusCostBreakdownRow } from "./carePlusCostBreakdownRows";

// HL-11359: "Kostenaufstellung" cost breakdown for Care+ Weight Loss checkout
// (Figma 5200:114443). Sits below the cart card on both the Shipping and the Payment page.
// Presentational only — no cart-context reads and no price math, same convention as
// CarePlusShippingCartCard. Rows are built by carePlusCostBreakdownRows.

interface CarePlusCostBreakdownProps {
  t: Translate;
  rows: CarePlusCostBreakdownRow[];
  currencyLocale: string;
  currencyRate: number;
  defaultOpen?: boolean;
  /** Bump to open the panel from outside (PaymentV3CarePlus "show cost details" link). */
  openRequest?: number;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
}

const CarePlusCostBreakdown = ({
  t,
  rows,
  currencyLocale,
  currencyRate,
  defaultOpen = false,
  openRequest = 0,
  onToggle,
  className = "",
}: CarePlusCostBreakdownProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    if (openRequest > 0) {
      setIsOpen((current) => {
        if (!current) onToggle?.(true);
        return true;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);
  const price = (amount: number) => getPrice(amount, currencyLocale, currencyRate);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    onToggle?.(next);
  };

  return (
    <div className={`care-plus-cost-breakdown flex w-full flex-col gap-[16px] ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        className="flex w-full items-start justify-between border-none bg-transparent p-0 text-left"
      >
        <span className="text-[14px] font-medium text-[#616975]">
          {t("costs_breakdown", "Cost breakdown")}
        </span>
        <span className="text-[12px] text-[#8a9099] underline">
          {isOpen ? t("hide_details", "Hide details") : t("show_details", "Show details")}
        </span>
      </button>

      {isOpen && (
        <div className="care-plus-cost-breakdown__panel flex w-full items-center rounded-[13px] border border-solid border-[#dee3ed] bg-white p-[14px]">
          <div className="flex min-w-0 flex-1 flex-col">
            {rows.map((row, index) => {
              // A line is only struck through when the customer is charged less than it lists for
              // (plan covers it, free delivery, waived service fee). Otherwise one plain value.
              const isDiscounted = row.chargedPrice < row.listPrice;

              return (
                <Fragment key={row.key}>
                  <div className="care-plus-cost-breakdown__row flex w-full items-start justify-between py-[12px]">
                    <div className="flex min-w-0 flex-1 flex-col gap-[8px]">
                      <span className="text-[16px] font-medium text-[#08080a]">{row.title}</span>
                      <span className="text-[14px] leading-[19px] text-[#99a3b2]">{row.description}</span>
                    </div>
                    <div className="flex shrink-0 items-baseline justify-end gap-[4px] whitespace-nowrap">
                      {isDiscounted && (
                        <span className="text-[10px] text-[#99a3b2] line-through">{price(row.listPrice)}</span>
                      )}
                      <span
                        className={`text-[16px] font-medium ${isDiscounted || row.isDiscount ? "text-[#61a333]" : "text-[#08080a]"}`}
                      >
                        {row.isDiscount ? `-${price(row.chargedPrice)}` : price(row.chargedPrice)}
                      </span>
                    </div>
                  </div>
                  {index < rows.length - 1 && (
                    <div className="care-plus-cost-breakdown__divider h-px w-full origin-top scale-y-50 bg-[#dee3ed]" />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarePlusCostBreakdown;
